import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnquiry } from "@/context/EnquiryContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Seo from "@/components/site/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Trash2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function EnquiryPage() {
  const { items, setQty, remove, clear } = useEnquiry();
  const nav = useNavigate();
  const [form, setForm] = useState({ customer_name: "", company_name: "", phone: "", whatsapp: "", email: "", requirements: "", preferred_contact: "phone" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.customer_name || !form.phone) { toast.error("Name and phone are required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, email: form.email || null, items: items.map(i => ({ product_id: i.product_id, product_name: i.product_name, quantity: i.quantity })) };
      const { data } = await api.post("/enquiries", payload);
      clear();
      toast.success(`Enquiry submitted: ${data.enquiry_id}`);
      nav("/enquiry/success?id=" + data.enquiry_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <Seo page="enquiry"/>
      <div className="bg-[#0F172A] text-white py-10">
        <div className="container-x">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Your Enquiry</h1>
          <p className="text-slate-300 mt-1">Add products with quantities and submit as one enquiry.</p>
        </div>
      </div>
      <div className="container-x py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {items.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-md p-10 text-center">
              <div className="font-heading font-bold text-lg">Your enquiry list is empty</div>
              <p className="text-slate-500 mt-1">Browse products and add items with quantities.</p>
              <Button className="btn-orange mt-4" onClick={() => nav("/products")}>Browse Products</Button>
            </div>
          ) : items.map(i => (
            <div key={i.product_id} className="bg-white border border-[#E2E8F0] rounded-md p-4 flex gap-4 items-center" data-testid={`enquiry-item-${i.product_id}`}>
              {i.image && <img src={i.image} alt="" className="w-16 h-16 rounded object-cover"/>}
              <div className="flex-1">
                <div className="font-semibold text-slate-900">{i.product_name}</div>
                <div className="text-xs text-slate-500 mt-1">Wholesale price on enquiry</div>
              </div>
              <div className="flex items-center border border-[#E2E8F0] rounded-md">
                <button className="px-2 py-1" onClick={() => setQty(i.product_id, i.quantity - 1)}><Minus className="w-3 h-3"/></button>
                <input value={i.quantity} onChange={e => setQty(i.product_id, parseInt(e.target.value)||1)} className="w-12 text-center outline-none"/>
                <button className="px-2 py-1" onClick={() => setQty(i.product_id, i.quantity + 1)}><Plus className="w-3 h-3"/></button>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(i.product_id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-md p-6">
          <h3 className="font-heading font-bold text-lg mb-4">Submit Requirement</h3>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.customer_name} onChange={e => setForm(f => ({...f, customer_name: e.target.value}))} data-testid="enq-name"/></div>
            <div><Label>Company / Business</Label><Input value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} data-testid="enq-company"/></div>
            <div><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="enq-phone"/></div>
            <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={e => setForm(f => ({...f, whatsapp: e.target.value}))} data-testid="enq-whatsapp"/></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="enq-email"/></div>
            <div><Label>Additional Requirements</Label><Textarea rows={3} value={form.requirements} onChange={e => setForm(f => ({...f, requirements: e.target.value}))} data-testid="enq-requirements"/></div>
            <div>
              <Label>Preferred Contact</Label>
              <RadioGroup value={form.preferred_contact} onValueChange={v => setForm(f => ({...f, preferred_contact: v}))} className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="phone"/> Phone</label>
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="whatsapp"/> WhatsApp</label>
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="email"/> Email</label>
              </RadioGroup>
            </div>
            <Button className="btn-orange w-full" onClick={submit} disabled={submitting || items.length === 0} data-testid="enq-submit">
              {submitting ? "Submitting..." : "Submit Requirement"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
